import React, { useState, useEffect } from 'react';
import Navbar from '../../layout/Navbar';
import SettingsMenu from '../../layout/SettingsMenu';
import JiraIntegrationForm from './JiraIntegrationForm';
import { checkJiraStatus, initJiraAuth } from '../../../api/integrations';
import { useLocation } from 'react-router-dom';

const IntegrationCard = ({ title, description, onConnect, isConnected, onManage, isLoading }) => {
  if (isConnected) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Connected
          </span>
        </div>
        <p className="text-gray-600 mb-4">{description}</p>
        <button
          onClick={onManage}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg 
            className="mr-2 h-5 w-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" 
            />
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" 
            />
          </svg>
          Manage {title}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h2 className="text-xl font-semibold mb-2">{title}</h2>
      <p className="text-gray-600 mb-4">{description}</p>
      <div className="p-4 mb-6 bg-blue-50 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3 flex-1 md:flex md:justify-between">
            <p className="text-sm text-blue-700">
              You will be redirected to Atlassian to authorize access to your Jira account.
            </p>
          </div>
        </div>
      </div>
      <button
        onClick={onConnect}
        disabled={isLoading}
        className={`w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
          isLoading ? 'opacity-75 cursor-not-allowed' : ''
        }`}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Redirecting to Atlassian...
          </>
        ) : (
          <>
            <svg 
              className="mr-2 h-5 w-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M12 4v16m8-8H4" 
              />
            </svg>
            Connect {title}
          </>
        )}
      </button>
    </div>
  );
};

const IntegrationsPage = () => {
  const location = useLocation();
  const [activeIntegration, setActiveIntegration] = useState(null);
  const [jiraStatus, setJiraStatus] = useState({
    isConnected: false,
    loading: true
  });
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [lastChecked, setLastChecked] = useState(Date.now());

  // Function to check Jira connection status
  const checkJiraConnection = async () => {
    try {
      setJiraStatus(prev => ({ ...prev, loading: true }));
      const response = await checkJiraStatus();
      setJiraStatus({
        isConnected: response.is_connected,
        loading: false
      });
      setLastChecked(Date.now());
    } catch (error) {
      console.error('Failed to check JIRA connection status:', error);
      setJiraStatus({
        isConnected: false,
        loading: false
      });
    }
  };

  // Check connection status when component mounts
  useEffect(() => {
    checkJiraConnection();
  }, []);

  // Check connection status when returning from auth flow
  // This detects when we return from Atlassian
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('status');
    const fromAuth = params.get('from_auth');
    
    // Check if we returned from auth flow
    if ((status === 'success' && fromAuth === 'jira') || 
        localStorage.getItem('jira_auth_pending') === 'true') {
      // Clear the pending auth flag
      localStorage.removeItem('jira_auth_pending');
      
      // Check connection status
      checkJiraConnection();
      
      // Clean up URL params if needed
      if (status || fromAuth) {
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
      }
    }
  }, [location]);

  // Set up polling to periodically check connection status
  useEffect(() => {
    // After authentication attempt, poll to verify the connection
    if (isAuthenticating) {
      const pollTimer = setInterval(() => {
        checkJiraConnection();
        
        // If we've confirmed the connection, stop polling
        if (jiraStatus.isConnected) {
          setIsAuthenticating(false);
          clearInterval(pollTimer);
        }
      }, 3000); // Check every 3 seconds
      
      // Stop polling after a reasonable timeout (30 seconds)
      const timeoutId = setTimeout(() => {
        clearInterval(pollTimer);
        setIsAuthenticating(false);
      }, 30000);
      
      return () => {
        clearInterval(pollTimer);
        clearTimeout(timeoutId);
      };
    }
  }, [isAuthenticating, jiraStatus.isConnected]);

  // Handle the Connect to Jira button click
  const handleConnectToJira = async () => {
    try {
      setIsAuthenticating(true);
      
      // Store the return URL in localStorage
      const returnUrl = `${window.location.origin}/me/integrations?status=success&from_auth=jira`;
      localStorage.setItem('jira_return_to', returnUrl);
      
      // Set flag to detect return from auth flow
      localStorage.setItem('jira_auth_pending', 'true');
      
      const { auth_url } = await initJiraAuth();
      
      // Display authenticating UI for a moment before redirect
      setTimeout(() => {
        // Redirect to Atlassian auth URL
        window.location.href = auth_url;
      }, 1000); // Short delay to show loading state
      
    } catch (error) {
      console.error('Failed to initialize Jira auth:', error);
      setIsAuthenticating(false);
    }
  };

  const handleConnect = (integration) => {
    setActiveIntegration(integration);
  };

  const handleBack = () => {
    // Refresh JIRA status when coming back from the integration page
    checkJiraConnection();
    setActiveIntegration(null);
  };

  const renderContent = () => {
    if (activeIntegration === 'JIRA' && jiraStatus.isConnected) {
      return <JiraIntegrationForm onBack={handleBack} />;
    }

    return (
      <>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Integrations</h1>
        <p className="text-gray-600 mb-8">Connect your Darknore account with third-party services</p>
        
        {jiraStatus.loading ? (
          <div className="flex justify-center items-center h-32">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <IntegrationCard
              title="JIRA"
              description="Connect to JIRA for issue tracking and project management"
              onConnect={jiraStatus.isConnected ? () => handleConnect('JIRA') : handleConnectToJira}
              onManage={() => handleConnect('JIRA')}
              isConnected={jiraStatus.isConnected}
              isLoading={isAuthenticating}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="flex">
        <SettingsMenu />
        <div className="flex-1 py-8 px-6">
          <div className="max-w-5xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationsPage; 